import React, { useState, useCallback, useMemo } from 'react';
import jwtDecode from 'jwt-decode';
import { OverlayVideoPlayer } from '../components/Video/OverlayVideoPlayer';
import { RequireLoginModal } from '../components/RequireLoginModal';

export const UserContext = React.createContext({});

const checkIfTokensIsValid = (decodedAccessToken) => {
  return (
    decodedAccessToken &&
    (decodedAccessToken.exp - 180) * 1000 > new Date().getTime()
  );
};

const getLoggedInUser = () => {
  const userFromLocalStorage = localStorage.getItem('user');
  if (!userFromLocalStorage) {
    return null;
  }

  const user = JSON.parse(userFromLocalStorage);
  const { accessToken } = user;
  const decodedAccessToken = jwtDecode(accessToken);

  user.decodedAccessToken = decodedAccessToken;

  const isTokenValid = checkIfTokensIsValid(decodedAccessToken);

  if (!isTokenValid) {
    localStorage.removeItem('user');
    return null;
  }

  return user;
};

function UserProvider({ children }) {
  const [video, setVideo] = useState(null);
  const [isPlayingVideo, setPlayingVideo] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const getUser = useCallback(getLoggedInUser);

  const isUserLoggedIn = useCallback(() => !!getLoggedInUser());

  const login = useCallback(() => {
    localStorage.setItem('authRedirectUrl', window.location.href);
    window.location.href = '/auth/login';
  });

  const logout = useCallback(() => {
    const user = getLoggedInUser();
    if (user) {
      localStorage.removeItem('user');
      window.location.href = `/auth/logout?idToken=${user.idToken}`;
    }
  });

  const playVideo = useCallback((v) => {
    const isAllowed = isUserLoggedIn();
    if (isAllowed) {
      setVideo(v);
      setPlayingVideo(true);
    } else {
      setShowModal(true);
    }
  });

  const closeVideo = useCallback(() => {
    setPlayingVideo(false);
    setVideo(null);
  });

  const getIdToken = useCallback(() => {
    const user = getLoggedInUser();
    return fetch('/auth/idtoken', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${user.accessToken}`,
      },
    })
      .then((res) => res.json())
      .then((res) => {
        return res.data.idToken;
      })
      .catch((err) => {
        console.error(err);
      });
  });

  const contextValue = useMemo(
    () => ({
      getUser,
      playVideo,
      closeVideo,
      login,
      logout,
      isUserLoggedIn,
      isUserAllowedToPlayVideo: isUserLoggedIn,
      getIdToken,
    }),
    [getUser, playVideo, closeVideo, logout, isUserLoggedIn, getIdToken]
  );

  return (
    <UserContext.Provider value={contextValue}>
      {isPlayingVideo && (
        <OverlayVideoPlayer closeVideo={closeVideo} video={video} />
      )}
      {showModal && (
        <RequireLoginModal
          closeModal={() => setShowModal(false)}
          login={login}
          heading="VIDEOKLIPP"
          message="För att ta del av videoklipp behöver du logga in på ditt
        Fotbollskonto."
        />
      )}
      {children}
    </UserContext.Provider>
  );
}

export default UserProvider;
